import { SimpleTable } from '@components/tables'
import { ChipCell } from '@components/tables/cells'
import { Grid, Paper } from '@mui/material'
import { GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import { ExamDto } from '@services/api'
import { Actions } from '@state/actions'
import { useSelector } from '@state/store'
import {
  findAll as findAllExam,
  findAllType,
} from '@state/thunks/procedureThunk'
import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

export interface ExamSelectionTableProps {
  onSelectionChange: (selectedRows: number[] | string[]) => void
  selectedRows: number[] | string[]
  excludedExams?: ExamDto[]
}
export const ExamSelectionTable: React.FC<ExamSelectionTableProps> = ({
  onSelectionChange,
  selectedRows,
  excludedExams,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const debounceRef = useRef<_.DebouncedFunc<any> | null>(null)

  const _onSelectionChange = (selection: GridRowId[]) => {
    onSelectionChange(selection as number[] | string[])
  }

  const { loading, exams } = useSelector(({ procedure, loading }) => ({
    exams: procedure.exams.datas,
    examType: procedure.examTypes,
    loading:
      loading[Actions.PROCEDURE_FIND_ALL] ||
      loading[Actions.PROCEDURE_CREATE] ||
      loading[Actions.PROCEDURE_UPDATE],
  }))

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'code',
        headerName: intl.formatMessage({ id: 'code' }),
        flex: 1,
      },
      {
        field: 'label',
        headerName: intl.formatMessage({ id: 'label' }),
        flex: 3,
      },
      {
        field: 'sex',
        headerName: intl.formatMessage({ id: 'gender' }),
        flex: 1,
      },
      {
        field: 'bodyPart',
        headerName: intl.formatMessage({ id: 'bodyPart' }),
        flex: 1,
        renderCell: ChipCell,
      },
      {
        field: 'ageRange',
        headerName: intl.formatMessage({ id: 'ageRange' }),
        flex: 1,
      },
      {
        field: 'contrast',
        headerName: intl.formatMessage({ id: 'contrast' }),
        type: 'boolean',
      },
      {
        field: 'dose',
        headerName: intl.formatMessage({ id: 'dose' }),
        type: 'boolean',
      },
    ],
    [intl],
  )

  const onRefresh = useCallback(() => {
    dispatch(findAllType())
    dispatch(findAllExam({}))
  }, [dispatch])

  useEffect(() => {
    onRefresh()
  }, [dispatch, onRefresh])

  const handleExamSearch = (value: string) => {
    const searchMemberDebounce = _.debounce(
      () => dispatch(findAllExam({ search: value })),
      350,
    )
    searchMemberDebounce()
    if (debounceRef.current) {
      debounceRef.current.cancel()
    }
    debounceRef.current = searchMemberDebounce
  }

  const filteredExams = useMemo(() => {
    if (!excludedExams || excludedExams?.length === 0) {
      return exams
    }
    return exams.filter(
      (exam) => !excludedExams.find((ex) => ex.id === exam.id),
    )
  }, [exams])

  return (
    <Grid container style={{ height: '50vh' }}>
      <Paper
        elevation={1}
        style={{
          width: '100%',
          border: '1px solid rgba(0, 0, 0, 0.125)',
          backgroundColor: '#eef1f6',
        }}
      >
        <SimpleTable
          rows={filteredExams}
          columns={columns}
          loading={loading}
          simpleToolBarProps={{
            title: 'assignedExams',
            icon: 'fa fa-link',
            search: true,
            onSearch: handleExamSearch,
          }}
          checkboxSelection
          onSelectionModelChange={_onSelectionChange}
          selections={selectedRows}
        />
      </Paper>
    </Grid>
  )
}

export default ExamSelectionTable
